// src/componentes/AppLayout/sidebarConfig.js
//
// Sprint SIDEBAR: configuración declarativa del menú lateral.
// Antes los ítems estaban hardcoded como JSX repetido (165 líneas) en
// AppLayout. Ahora viven aquí como datos → agregar/mover/renombrar un ítem
// es editar este arreglo, sin tocar el render.
//
// Cada item:
//   id      — clave única (para keys de React)
//   label   — texto visible
//   route   — ruta destino (NO cambiar sin actualizar el router)
//   icon    — componente de react-icons
//   group   — null = top-level; "cobranza"|"catalogos"|"finanzas"|"proyectos"|"admin"
//   visible — (can, user) => boolean. Decide si el ítem se muestra.

import {
  FaClock,
  FaHome,
  FaUsers,
  FaUserTie,
  FaBuilding,
  FaCalendarAlt,
  FaProjectDiagram,
  FaHeadset,
  FaChartLine,
  FaHardHat,
  FaClipboardCheck,
  FaClipboardList,
  FaCashRegister,
  FaHandshake,
  FaBookmark,
  FaChartPie,
  FaPhoneSlash,
  FaFileInvoiceDollar,
  FaUserShield,
  FaSlidersH,
  FaSearch,
  FaCalendarCheck,
} from "react-icons/fa";
import { IA_FLAGS } from "../../helpers/iaFlags";

// Orden y metadatos de los grupos acordeón.
export const SIDEBAR_GROUPS = [
  { id: "cobranza", label: "Cobranza", icon: FaCalendarCheck },
  { id: "catalogos", label: "Catálogos", icon: FaBuilding },
  { id: "finanzas", label: "Finanzas", icon: FaChartLine },
  { id: "proyectos", label: "Proyectos", icon: FaProjectDiagram },
  { id: "admin", label: "Admin", icon: FaUserShield },
];

export const SIDEBAR_ITEMS = [
  // ── Top-level (siempre arriba, sin colapsar) ──────────────────────────
  { id: "dashboard", label: "Dashboard", route: "/app/dashboard", icon: FaHome, group: null, visible: () => true },
  { id: "clientes", label: "Clientes", route: "/app/clientes", icon: FaUsers, group: null, visible: (can) => can("clientes.ver") },
  { id: "agenda", label: "Agenda de cobros", route: "/app/agenda", icon: FaCalendarAlt, group: null, visible: (can) => can("agenda.ver") },
  { id: "seguimiento", label: "Seguimiento cobranza", route: "/app/cobranza/seguimiento", icon: FaClipboardList, group: null, visible: (can) => can("cobranza.promesas.ver") },
  { id: "plandia", label: "Asistente de cobranza 🤖", route: "/app/plan-dia", icon: FaHeadset, group: null, visible: (can) => can("cobranzaInteligente.ver") },

  // ── Cobranza ──────────────────────────────────────────────────────────
  { id: "agendaPrev", label: "Agenda previsoria", route: "/app/agenda/previsoria", icon: FaClock, group: "cobranza", visible: (can) => can("agenda.ver") },

  // ── Catálogos ─────────────────────────────────────────────────────────
  { id: "propiedades", label: "Propiedades", route: "/app/propiedades", icon: FaBuilding, group: "catalogos", visible: (can) => can("propiedades.ver") },
  { id: "socios", label: "Socios", route: "/app/socios", icon: FaUserTie, group: "catalogos", visible: (can) => can("socios.ver") },
  { id: "apartados", label: "Apartados", route: "/app/apartados", icon: FaBookmark, group: "catalogos", visible: (can) => can("deudas.ver") },
  { id: "telInv", label: "Teléfonos inválidos", route: "/app/telefonos-invalidos", icon: FaPhoneSlash, group: "catalogos", visible: (can) => can("clientes.ver") },

  // ── Finanzas ──────────────────────────────────────────────────────────
  { id: "reportes", label: "Reportes", route: "/app/reportes", icon: FaChartPie, group: "finanzas", visible: (can) => can("reportes.ver") },
  { id: "erp", label: "ERP", route: "/app/erp", icon: FaChartLine, group: "finanzas", visible: (can) => can("erp.ver") },
  { id: "caja", label: "Caja general", route: "/app/cajas/general", icon: FaCashRegister, group: "finanzas", visible: (can) => can("cajas.general.ver") || can("cajas.ver") },
  { id: "comisiones", label: "Comisiones", route: "/app/comisiones", icon: FaHandshake, group: "finanzas", visible: (can) => can("comisiones.ver") },
  // Rename 2026-06-17: "Gestión interna" → "Cuentas por pagar" (misma ruta /app/interna).
  { id: "cxp", label: "Cuentas por pagar", route: "/app/interna", icon: FaFileInvoiceDollar, group: "finanzas", visible: (can) => can("gestionInterna.ver") },

  // ── Proyectos ─────────────────────────────────────────────────────────
  { id: "proyectos", label: "Proyectos", route: "/app/proyectos", icon: FaProjectDiagram, group: "proyectos", visible: (can) => can("proyectos.ver") },
  { id: "obras", label: "Obras", route: "/app/obras", icon: FaHardHat, group: "proyectos", visible: (can) => can("obras.ver") },

  // ── Admin ─────────────────────────────────────────────────────────────
  { id: "usuarios", label: "Usuarios / Roles", route: "/app/usuarios", icon: FaUserShield, group: "admin", visible: (can, user) => can("usuarios.ver") || user?.rol === "admin" },
  { id: "auditoria", label: "Auditoría", route: "/app/auditoria", icon: FaClipboardCheck, group: "admin", visible: (can, user) => can("auditoria.ver") || user?.rol === "admin" },
  { id: "configEmpresa", label: "Config. empresa (IA)", route: "/app/configuracion-empresa", icon: FaSlidersH, group: "admin", visible: (can, user) => IA_FLAGS.menuConfigEmpresa && (can("gestionInterna.ver") || user?.rol === "admin") },
  { id: "calidadDatos", label: "Calidad datos (IA)", route: "/app/calidad-datos", icon: FaSearch, group: "admin", visible: (can, user) => IA_FLAGS.menuCalidadDatos && (can("gestionInterna.ver") || user?.rol === "admin") },
];

// Default inteligente por rol: qué grupos arrancan abiertos la PRIMERA vez
// (antes de que el usuario los toque; luego manda localStorage).
//   - Perfil admin/ejecutivo (ve ERP o Usuarios): Catálogos + Finanzas abiertos.
//   - Perfil cobrador (operativo): solo Cobranza abierto.
export function defaultGroupsOpen(user, can) {
  const esGestion = user?.rol === "admin" || can("erp.ver") || can("usuarios.ver");
  if (esGestion) {
    return { cobranza: false, catalogos: true, finanzas: true, proyectos: false, admin: false };
  }
  return { cobranza: true, catalogos: false, finanzas: false, proyectos: false, admin: false };
}
